import Head from "next/head";

import { BASE_URL, buildAbsoluteUrl } from "@lib/seoUrls";

export interface BluetoothPageMetadataProps {
  title: string;
  description: string;
  canonicalPath: string;
  lang: "pl" | "en";
  ogLocale: string;
  structuredData?: Record<string, unknown>[];
  alternatePaths?: {
    pl?: string;
    en?: string;
    xDefault?: string;
  };
}

export function BluetoothPageMetadata({
  title,
  description,
  canonicalPath,
  ogLocale,
  structuredData = [],
  alternatePaths = {
    pl: "/aplikacje-bluetooth",
    en: "/mobile-app-with-bluetooth",
    xDefault: "/aplikacje-bluetooth",
  },
}: BluetoothPageMetadataProps) {
  const canonicalUrl = buildAbsoluteUrl(canonicalPath);
  const fullTitle = `${title} | bluesBrackets`;
  const plUrl = alternatePaths.pl
    ? buildAbsoluteUrl(alternatePaths.pl)
    : undefined;
  const enUrl = alternatePaths.en
    ? buildAbsoluteUrl(alternatePaths.en)
    : undefined;
  const xDefaultUrl = alternatePaths.xDefault
    ? buildAbsoluteUrl(alternatePaths.xDefault)
    : undefined;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {plUrl && <link rel="alternate" hrefLang="pl" href={plUrl} />}
      {enUrl && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {xDefaultUrl && (
        <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />
      )}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={`${BASE_URL}/og-dafault.png`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${BASE_URL}/og-dafault.png`} />

      <link rel="icon" type="image/png" href="/favicon-dark.png" />
      <meta name="theme-color" content="#0f172a" />

      {structuredData.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Head>
  );
}
